/**
 * Receipt encoding / decoding
 *
 * All receipt data lives in the URL hash so no server is needed.
 * The hash is a Base64url-encoded JSON blob.
 */

export interface ReceiptLineItem {
  name: string;
  qty: number;
  price: number; // unit fiat price
}

export interface ReceiptData {
  /** Receipt schema version */
  v: 1;
  merchantName: string;
  merchantTagline?: string;
  /** Base64-encoded logo PNG — only included if merchant has a logo */
  logoDataUrl?: string;
  brandColor?: string;
  currency: string;
  subtotal: number;
  discountAmount: number;
  discountLabel: string;
  taxAmount: number;
  taxRate: number;
  taxName: string;
  tipAmount: number;
  fiatAmount: number; // grand total = subtotal - discount + tax + tip
  bchAmount: number;
  note?: string;
  txid: string;
  address: string;
  timestamp: number;
  /** Settlement asset — absent means BCH (back-compat). */
  asset?: 'BCH' | 'PUSD';
  tokenAmount?: number; // stablecoin display amount (e.g. 5.00)
  tokenSymbol?: string; // e.g. "PUSD"
  /** Line items from the cart — omitted for keypad-only sales */
  items?: ReceiptLineItem[];
}

function toBase64Url(str: string): string {
  return btoa(unescape(encodeURIComponent(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function fromBase64Url(str: string): string {
  const padded = str.replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  return decodeURIComponent(escape(atob(padded + pad)));
}

export function encodeReceipt(data: ReceiptData): string {
  return toBase64Url(JSON.stringify(data));
}

export function decodeReceipt(hash: string): ReceiptData | null {
  try {
    // Strip leading '#' if present
    const clean = hash.startsWith('#') ? hash.slice(1) : hash;
    const parsed = JSON.parse(fromBase64Url(clean));
    if (parsed?.v !== 1) return null;
    return parsed as ReceiptData;
  } catch {
    return null;
  }
}

export function buildReceiptUrl(data: ReceiptData): string {
  const encoded = encodeReceipt(data);
  const base = window.location.origin;
  return `${base}/receipt#${encoded}`;
}
